import { useLazyQuery, useQuery } from "@apollo/client";
import equal from "deep-equal";
import { motion } from "framer-motion";
import { ComponentProps, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { StringParam, useQueryParams } from "use-query-params";

import EmployerBenefitCard from "@components/explore/EmployerBenefitCard";
import styles from "@components/explore/Explore.module.css";
import ExploreHeader from "@components/explore/ExploreHeader";
import ExploreFilters from "@components/forms/ExploreFilters";
import HandleQuery from "@components/HandleQuery";
import ExploreMarketplaceLoader from "@components/loaders/ExploreMarketplaceLoader";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useScrollPosition from "@hooks/useScrollPosition";
import { EventTypeEnum, ExploreSectionsEnum, ScrollPositionPageTypeEnum } from "@typing/Enums";
import {
  AppEventActionKindEnum,
  AppEventCreateDocument,
  AppEventCreateMutation,
  AppEventCreateMutationVariables,
  GetEmployerBenefitsDocument,
  GetEmployerBenefitsQuery,
  GetEmployerBenefitsQueryVariables,
  GetJourneyWithEmployerBenefitCategoriesDocument,
  GetJourneyWithEmployerBenefitCategoriesQuery,
  GetJourneyWithEmployerBenefitCategoriesQueryVariables
} from "@typing/Generated";

type Filters = ComponentProps<typeof ExploreFilters>["filters"];

type BenefitEdge = NonNullable<
  NonNullable<NonNullable<GetEmployerBenefitsQuery["employerBenefits"]["edges"]>[number]>["node"]
>;

type Props = {
  journeyId: string;
};

const Marketplace = ({ journeyId }: Props) => {
  const myExpert = useMyExpert();
  const history = useHistory();

  const [filters, setFilters] = useState<Filters>({
    articleContentType: undefined,
    eventType: EventTypeEnum.UPCOMING,
    search: ""
  });

  const journeyQuery = useQuery<
    GetJourneyWithEmployerBenefitCategoriesQuery,
    GetJourneyWithEmployerBenefitCategoriesQueryVariables
  >(GetJourneyWithEmployerBenefitCategoriesDocument, {
    variables: {
      id: journeyId
    }
  });

  const getJourney = useQuery<
    GetJourneyWithEmployerBenefitCategoriesQuery,
    GetJourneyWithEmployerBenefitCategoriesQueryVariables
  >(GetJourneyWithEmployerBenefitCategoriesDocument, {
    variables: {
      id: journeyId
    }
  });

  const [getEmployerBenefits, employerBenefitsQuery] = useLazyQuery<
    GetEmployerBenefitsQuery,
    GetEmployerBenefitsQueryVariables
  >(GetEmployerBenefitsDocument);

  useEffect(() => {
    if (getJourney.data?.journey.owner.employer?.hideBenefits && !myExpert) {
      history.push(`/client/journeys/${journeyId}/care-plan`);
    }
  });

  useEffect(() => {
    if (myExpert || !getJourney.data?.journey.owner.employer?.hideBenefits) {
      getEmployerBenefits({
        variables: {
          category: filters.category,
          count: 30,
          journeyId,
          search: filters.search
        }
      });
    }
  }, [
    filters.search,
    getEmployerBenefits,
    filters.category,
    journeyId,
    myExpert,
    getJourney.data?.journey.owner.employer?.hideBenefits
  ]);

  useEffect(() => {
    if (
      journeyId &&
      !employerBenefitsQuery.loading &&
      employerBenefitsQuery.data?.employerBenefits.pageInfo.hasNextPage
    )
      employerBenefitsQuery
        .fetchMore({
          variables: {
            category: filters.category,
            journeyId,
            search: filters.search
          }
        })
        .then(res => {
          const edges = res.data.employerBenefits?.edges;
          const result: BenefitEdge[] = [];

          if (edges) {
            edges.forEach(edge => {
              if (edge?.node) {
                result.push(edge.node);
              }
            });
          }
        });
  }, [employerBenefitsQuery, filters.category, filters.search, journeyId]);

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const [query] = useQueryParams({
    benefitId: { ...StringParam, urlName: "benefitId" }
  });

  useScrollPosition(ScrollPositionPageTypeEnum.MARKETPLACE, !employerBenefitsQuery.loading);

  const allBenefits = useNodesFromConnection(employerBenefitsQuery.data?.employerBenefits);

  const [selectedBenefitId, setSelectedBenefitId] = useState<string | null | undefined>(query.benefitId);

  const [createAppEvent] = useMutationWithValidation<AppEventCreateMutation, AppEventCreateMutationVariables>(
    AppEventCreateDocument,
    "appEvent"
  );

  const generateHandleEmployerBenefitClick = (employerBenefitId: string) => () => {
    if (selectedBenefitId === employerBenefitId) {
      setSelectedBenefitId(null);
    } else {
      setSelectedBenefitId(employerBenefitId);
      createAppEvent({
        actionKind: AppEventActionKindEnum.employer_benefit_opened,
        journeyId,
        resourceId: employerBenefitId
      });
    }
  };

  return (
    <>
      <ExploreHeader exploreSection={ExploreSectionsEnum.MARKETPLACE} journeyId={journeyId} />
      <ExploreFilters
        categoriesToInclude={journeyQuery.data?.journey.clientEmployerBenefitCategories ?? []}
        exploreSection={ExploreSectionsEnum.MARKETPLACE}
        filters={filters}
        onChange={handleFilterChange}
      />
      <HandleQuery loadingContent={<ExploreMarketplaceLoader />} query={employerBenefitsQuery}>
        {_data => (
          <motion.div className={styles.marketplaceItems} key="marketplaceItems" layout="position">
            {allBenefits.map(benefit => (
              <motion.div
                className={benefit.id === selectedBenefitId ? styles.selectedBenefit : styles.unselectedBenefit}
                key={benefit.id}
                layout="position"
                onClick={generateHandleEmployerBenefitClick(benefit.id)}
              >
                <EmployerBenefitCard
                  employerBenefit={benefit}
                  expanded={selectedBenefitId == benefit.id}
                  journeyId={journeyId}
                  layout="vertical"
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </HandleQuery>
    </>
  );
};

export default Marketplace;
